import { del, get, post, put } from '@api/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ICommon, IDataResponse, IItemResponse } from 'types/common';
import { QueryKeyArg } from '.';

const EMPTY_DATA = {
	data: [],
	total: 0
};

export const httpPostItem = async <T extends Partial<ICommon>, R>(
	route: string,
	reqItem: Partial<T>
) => {
	const res = await post<Partial<T>, R>(route, reqItem);
	return res.data;
};

export const httpPutItem = async <T extends Partial<ICommon>, R>(
	route: string,
	reqItem: Partial<T>
) => {
	const res = await put<Partial<T>, R>(route, reqItem);
	return res.data;
};

export const httpDeleteItem = async (route: string) => {
	await del(route);
};

export const getItem = async <T extends Partial<ICommon>>(route: string) => {
	const res = await get<T>(route);
	return res.data;
};

export const getItems = async <T extends Partial<ICommon>>(route: string) => {
	const res = await get<IDataResponse<T>>(route);
	return res.data;
};

export const useCreateItem = <T extends Partial<ICommon>>(
	itemsName: string,
	route: string
) => {
	const queryClient = useQueryClient();
	return useMutation<IItemResponse<T>, unknown, T>(
		async (item: Partial<T>) => httpPostItem(route, item),
		{
			onSuccess: ({ item }: IItemResponse<T>) => {
				if (item) {
					const allItems =
						queryClient.getQueryData<IDataResponse<T>>([itemsName]) ||
						EMPTY_DATA;

					queryClient.setQueryData([itemsName], {
						...allItems,
						data: [...(allItems.data || []), item],
						total: allItems.total + 1
					});
				}
			}
		}
	);
};

export const useUpdateItem = <T extends Partial<ICommon>>(
	route: string,
	itemsKey: string
) => {
	const queryClient = useQueryClient();
	return useMutation<IItemResponse<T>, unknown, T>(
		async (item: T) => httpPutItem(route, item),
		{
			onSuccess: ({ item }: IItemResponse<T>) => {
				if (item) {
					const allItems =
						queryClient.getQueryData<IDataResponse<T>>([itemsKey]) ||
						EMPTY_DATA;

					queryClient.setQueryData([itemsKey], {
						...allItems,
						data: [...(allItems.data || []), item]
					});
				}
			}
		}
	);
};

export const useDeleteItem = <R extends Partial<ICommon>>(
	itemId: string,
	itemsName: string,
	route: string
) => {
	const queryClient = useQueryClient();
	return useMutation(() => httpDeleteItem(route), {
		onSuccess: () => {
			const allItems =
				queryClient.getQueryData<IDataResponse<R>>([itemsName]) || EMPTY_DATA;

			queryClient.setQueryData([itemsName], {
				...allItems,
				data: (allItems.data || []).filter((item) => item.id !== itemId),
				total: allItems.total - 1
			});
		}
	});
};

export const useItems = <T extends Partial<ICommon>>(
	key: string,
	route: string
) => useQuery([key], () => getItems<T>(route));

export const useItem = <T extends Partial<ICommon>>(
	key: string,
	route: string
) => useQuery([key], () => getItem<T>(route));

export const useUpdateSingleItem = <T extends Partial<ICommon>>(
	key: QueryKeyArg,
	route: string,
	httpFunc: (route: string, reqItem: Partial<T>) => Promise<T>
) => {
	const queryClient = useQueryClient();
	return useMutation<T, unknown, T>(async (item: T) => httpFunc(route, item), {
		onSuccess: async (data) => {
			queryClient.setQueryData([key], data);
		}
	});
};
